import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Settings as SettingsIcon,
  Sensors as SensorsIcon,
  Label as LabelIcon,
  People as PeopleIcon,
  PersonRemove,
  Star,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';

const ProjectSettings = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { user } = useAuth();
  const {
    sensors,
    labels,
    manufacturers,
    sensorModels,
    collaborators,
    currentProject,
    fetchSensors,
    fetchLabels,
    fetchManufacturers,
    fetchSensorModels,
    fetchCollaborators,
    addCollaborator,
    removeCollaborator,
    fetchProject,
    createSensor,
    updateSensor,
    deleteSensor,
    createLabel,
    updateLabel,
    deleteLabel,
  } = useProject();

  // Sensor dialog state
  const [sensorDialogOpen, setSensorDialogOpen] = useState(false);
  const [editingSensor, setEditingSensor] = useState(null);
  const [sensorForm, setSensorForm] = useState({
    name: '',
    settings: '',
    model: '',
  });
  const [selectedManufacturer, setSelectedManufacturer] = useState('');

  // Label dialog state
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelForm, setLabelForm] = useState({
    name: '',
    description: '',
    label_type: 'Numeric',
    choices: [],
  });
  const [newCategory, setNewCategory] = useState('');

  // Collaborator state
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const [addingCollaborator, setAddingCollaborator] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (projectId) {
        await fetchProject(projectId);
        const sensorsData = await fetchSensors(projectId);
        if (sensorsData?.length > 0) {
          console.log('First sensor:', sensorsData[0]);
        }
        await fetchLabels(projectId);
        await fetchCollaborators(projectId);
        const manufacturersData = await fetchManufacturers();

        // Load sensor models for all manufacturers so we can display them
        if (Array.isArray(manufacturersData)) {
          for (const manufacturer of manufacturersData) {
            await fetchSensorModels(manufacturer.id);
          }
        }
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Sensor handlers
  const handleOpenSensorDialog = (sensor = null) => {
    if (sensor) {
      setEditingSensor(sensor);
      setSensorForm({
        name: sensor.name,
        settings: sensor.settings,
        model: sensor.model?.id || '',
      });
      if (sensor.model?.manufacturer?.id) {
        setSelectedManufacturer(sensor.model.manufacturer.id);
        fetchSensorModels(sensor.model.manufacturer.id);
      }
    } else {
      setEditingSensor(null);
      setSensorForm({ name: '', settings: '', model: '' });
      setSelectedManufacturer('');
    }
    setSensorDialogOpen(true);
  };

  const handleCloseSensorDialog = () => {
    setSensorDialogOpen(false);
    setEditingSensor(null);
    setSensorForm({ name: '', settings: '', model: '' });
    setSelectedManufacturer('');
  };

  const handleManufacturerChange = async (manufacturerId) => {
    setSelectedManufacturer(manufacturerId);
    setSensorForm({ ...sensorForm, model: '' });
    if (manufacturerId) {
      await fetchSensorModels(manufacturerId);
    }
  };

  const handleSaveSensor = async () => {
    try {
      console.log('Saving sensor with form data:', sensorForm);
      console.log('Selected manufacturer:', selectedManufacturer);
      console.log('Available sensor models:', sensorModels[selectedManufacturer]);

      if (editingSensor) {
        await updateSensor(editingSensor.id, sensorForm);
        await fetchSensors(projectId);
      } else {
        const result = await createSensor(projectId, sensorForm);
        console.log('Sensor created:', result);
      }
      handleCloseSensorDialog();
    } catch (error) {
      console.error('Error saving sensor:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleDeleteSensor = async (sensorId) => {
    if (window.confirm('Are you sure you want to delete this sensor?')) {
      await deleteSensor(sensorId, projectId);
    }
  };

  // Label handlers
  const handleOpenLabelDialog = (label = null) => {
    if (label) {
      setEditingLabel(label);
      setLabelForm({
        name: label.name,
        description: label.description || '',
        label_type: label.label_type || 'Numeric',
        choices: (label.choices || []).map((c) => ({ name: c.choice || c.name })),
      });
    } else {
      setEditingLabel(null);
      setLabelForm({
        name: '',
        description: '',
        label_type: 'Numeric',
        choices: [],
      });
    }
    setLabelDialogOpen(true);
  };

  const handleCloseLabelDialog = () => {
    setLabelDialogOpen(false);
    setEditingLabel(null);
    setNewCategory('');
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && labelForm.choices.length < 15) {
      setLabelForm({
        ...labelForm,
        choices: [...labelForm.choices, { name: newCategory.trim() }],
      });
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (index) => {
    setLabelForm({
      ...labelForm,
      choices: labelForm.choices.filter((_, i) => i !== index),
    });
  };

  const handleSaveLabel = async () => {
    try {
      const rawChoices = labelForm.label_type === 'Categorical' ? labelForm.choices : [];

      if (editingLabel) {
        // Update expects [{choice: "X"}]
        const labelData = {
          ...labelForm,
          choices: rawChoices.map((c) => ({ choice: c.name })),
        };
        await updateLabel(editingLabel.id, labelData);
        await fetchLabels(projectId);
      } else {
        // Create expects ["X", "Y"]
        const labelData = {
          ...labelForm,
          choices: rawChoices.map((c) => c.name),
        };
        await createLabel(projectId, labelData);
      }
      handleCloseLabelDialog();
    } catch (error) {
      console.error('Error saving label:', error);
    }
  };

  const handleDeleteLabel = async (labelId) => {
    if (window.confirm('Are you sure you want to delete this label?')) {
      await deleteLabel(labelId, projectId);
    }
  };

  // Collaborator handlers
  const isOwner = currentProject && user && currentProject.project_owner_id === user.id;

  const handleAddCollaborator = async () => {
    if (!collaboratorEmail.trim()) return;
    setAddingCollaborator(true);
    try {
      await addCollaborator(projectId, collaboratorEmail.trim());
      setCollaboratorEmail('');
    } catch {
      // toast is handled in context
    } finally {
      setAddingCollaborator(false);
    }
  };

  const handleRemoveCollaborator = async (userId) => {
    if (window.confirm('Remove this collaborator from the project?')) {
      await removeCollaborator(projectId, userId);
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <SettingsIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            <Typography variant="h3" fontWeight={700}>
              Project Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure sensors, labels, and collaborators for your project
          </Typography>
        </Box>

        {/* Sensors Section */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SensorsIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    Sensors
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage spectroscopy sensors
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenSensorDialog()}
              >
                Add Sensor
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Settings</strong></TableCell>
                    <TableCell><strong>Model</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sensors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No sensors configured yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sensors.map((sensor) => {
                      // Handle both cases: model as ID (number) or model as object
                      const modelId = typeof sensor.model === 'object' ? sensor.model?.id : sensor.model;
                      let modelInfo = null;

                      // Look up model info from sensorModels state
                      if (modelId) {
                        for (const manufacturerId in sensorModels) {
                          const models = sensorModels[manufacturerId];
                          modelInfo = models.find(m => m.id === modelId);
                          if (modelInfo) break;
                        }
                      }

                      return (
                        <TableRow key={sensor.id} hover>
                          <TableCell>{sensor.name}</TableCell>
                          <TableCell>{sensor.settings}</TableCell>
                          <TableCell>
                            {modelInfo ? (
                              <Chip
                                label={modelInfo.name}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : modelId ? (
                              <Chip
                                label={`Model #${modelId}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenSensorDialog(sensor)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSensor(sensor.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Labels Section */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LabelIcon sx={{ fontSize: 32, color: theme.palette.secondary.main }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    Labels
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage classification labels
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                onClick={() => handleOpenLabelDialog()}
              >
                Add Label
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {labels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No labels configured yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    labels.map((label) => (
                      <TableRow key={label.id} hover>
                        <TableCell>{label.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={label.label_type || 'Numeric'}
                            size="small"
                            color={label.label_type === 'Categorical' ? 'secondary' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>{label.description || '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenLabelDialog(label)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteLabel(label.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Collaborators Section */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon sx={{ fontSize: 32, color: theme.palette.info.main }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    Collaborators
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage who has access to this project
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Add collaborator form */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Email address"
                placeholder="Enter collaborator's email"
                value={collaboratorEmail}
                onChange={(e) => setCollaboratorEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCollaborator();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddCollaborator}
                disabled={!collaboratorEmail.trim() || addingCollaborator}
                sx={{ minWidth: 120 }}
              >
                {addingCollaborator ? 'Adding...' : 'Add'}
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Role</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Project Owner row */}
                  {currentProject && (
                    <TableRow>
                      <TableCell>
                        {currentProject.project_owner || '-'}
                      </TableCell>
                      <TableCell>{currentProject.project_owner_email || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          icon={<Star sx={{ fontSize: 16 }} />}
                          label="Owner"
                          size="small"
                          color="warning"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Collaborators */}
                  {collaborators.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary" variant="body2">
                          No collaborators added yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    collaborators.map((collab) => (
                      <TableRow key={collab.id} hover>
                        <TableCell>
                          {[collab.first_name, collab.last_name].filter(Boolean).join(' ') || '-'}
                        </TableCell>
                        <TableCell>{collab.email}</TableCell>
                        <TableCell>
                          <Chip label="Collaborator" size="small" color="info" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveCollaborator(collab.id)}
                            title="Remove collaborator"
                          >
                            <PersonRemove fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Sensor Dialog */}
        <Dialog open={sensorDialogOpen} onClose={handleCloseSensorDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSensor ? 'Edit Sensor' : 'Add New Sensor'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Name"
                value={sensorForm.name}
                onChange={(e) => setSensorForm({ ...sensorForm, name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Settings"
                value={sensorForm.settings}
                onChange={(e) => setSensorForm({ ...sensorForm, settings: e.target.value })}
                required
              />
              <TextField
                select
                fullWidth
                label="Manufacturer"
                value={selectedManufacturer}
                onChange={(e) => handleManufacturerChange(e.target.value)}
                required
              >
                {manufacturers.map((manufacturer) => (
                  <MenuItem key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label="Model"
                value={sensorForm.model}
                onChange={(e) => setSensorForm({ ...sensorForm, model: e.target.value })}
                disabled={!selectedManufacturer}
                required
              >
                {(sensorModels[selectedManufacturer] || []).map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSensorDialog}>Cancel</Button>
            <Button
              onClick={handleSaveSensor}
              variant="contained"
              disabled={!sensorForm.name || !sensorForm.settings || !sensorForm.model}
            >
              {editingSensor ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Label Dialog */}
        <Dialog open={labelDialogOpen} onClose={handleCloseLabelDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingLabel ? 'Edit Label' : 'Add New Label'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Name"
                value={labelForm.name}
                onChange={(e) => setLabelForm({ ...labelForm, name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={labelForm.description}
                onChange={(e) => setLabelForm({ ...labelForm, description: e.target.value })}
                multiline
                rows={2}
              />
              <TextField
                select
                fullWidth
                label="Label Type"
                value={labelForm.label_type}
                onChange={(e) => setLabelForm({ ...labelForm, label_type: e.target.value, choices: [] })}
                required
              >
                <MenuItem value="Numeric">Numeric</MenuItem>
                <MenuItem value="Categorical">Categorical</MenuItem>
              </TextField>

              {labelForm.label_type === 'Categorical' && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary">
                    Categories (1-15)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Category Name"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCategory();
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddCategory}
                      disabled={!newCategory.trim() || labelForm.choices.length >= 15}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {labelForm.choices.map((choice, index) => (
                      <Chip
                        key={index}
                        label={choice.name}
                        onDelete={() => handleRemoveCategory(index)}
                        color="secondary"
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLabelDialog}>Cancel</Button>
            <Button
              onClick={handleSaveLabel}
              variant="contained"
              color="secondary"
              disabled={
                !labelForm.name ||
                (labelForm.label_type === 'Categorical' && labelForm.choices.length === 0)
              }
            >
              {editingLabel ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default ProjectSettings;
